import { initializeApp, getApps, getApp } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { firebaseConfig, isDevelopment } from '@/config/firebase.config'

// Initialize Firebase app (guard against double-init)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp()

// Export Firebase service instances
export const auth = getAuth(app)
/**
 * Shared Firestore instance used by all service functions.
 *
 * Initialized from the Firebase app singleton. In development mode,
 * automatically connected to the local Firestore emulator on port 8080
 * via `connectEmulators()`.
 */
export const db = getFirestore(app)
/**
 * Shared Firebase Storage instance for media uploads and downloads.
 *
 * Connected to the local Storage emulator on port 9199 in development mode.
 * Used by `storage.service.ts` for `uploadMedia`, `deleteMedia`, and `getMediaUrl`.
 */
export const storage = getStorage(app)
/**
 * Shared Cloud Functions instance for calling callable functions.
 *
 * Connected to the local Functions emulator on port 5001 in development mode.
 * Used to invoke `assignRole`, `resolveTestSession`, and `submitTestSession`.
 */
export const functions = getFunctions(app)

// Track emulator connection state
let emulatorsConnected = false

/**
 * Connect to Firebase Emulator Suite for local development.
 * Can only be called once per session.
 *
 * Port selection: reads VITE_EMULATOR_* env vars when present (set by the
 * `test:e2e:seeded` Vite server command to target the dedicated E2E emulator
 * on ports 19099/18080/19199/15001). Falls back to the standard dev ports
 * (9099/8080/9199/5001) used by `dev:all`.
 */
export function connectEmulators(): void {
  if (emulatorsConnected) return
  emulatorsConnected = true

  const authPort      = Number(import.meta.env.VITE_EMULATOR_AUTH_PORT)      || 9099
  const firestorePort = Number(import.meta.env.VITE_EMULATOR_FIRESTORE_PORT) || 8080
  const storagePort   = Number(import.meta.env.VITE_EMULATOR_STORAGE_PORT)   || 9199
  const functionsPort = Number(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT) || 5001

  connectAuthEmulator(auth, `http://localhost:${authPort}`, { disableWarnings: true })
  connectFirestoreEmulator(db, 'localhost', firestorePort)
  connectStorageEmulator(storage, 'localhost', storagePort)
  connectFunctionsEmulator(functions, 'localhost', functionsPort)
}

// Automatically connect emulators in development
if (isDevelopment) {
  connectEmulators()
}

export default app
